<i18n>
{
  "no": {
    "ACCOUNT_MISSING_EMAIL": "Kontoen mangler epostadresse",
    "ACCOUNT_MISSING_MOBILE": "Kontoen mangler mobilnummer",
    "TITLE": "Glemt brukernavn",
    "SEND_IT": "Send meg brukernavnet",
    "SUCCESS": "Brukernavnet er sent til kontaktinformasjonen du oppga. Gå tilbake til",
    "LOGIN": "innlogging",
    "links": {
      "forgotPassword": "Glemt passord",
      "forgotUsername": "Glemt brukernavn",
      "register": "Registrer ny bruker"
    },
    "otpFromSms": "Bekreftelseskode fra SMS",
    "otpSentToNumber": "Vi sendte en bekreftelseskode til ",
    "validate": "Valider kode"
  },
  "en": {
    "ACCOUNT_MISSING_EMAIL": "User account has no email address",
    "ACCOUNT_MISSING_MOBILE": "User account has no mobile number",
    "TITLE": "Forgot user name",
    "SEND_IT": "Send me the username",
    "SUCCESS": "The user name was sent. Go back to ",
    "LOGIN": "login",
    "links": {
      "forgotPassword": "Forgot password",
      "forgotUsername": "Forgot username",
      "register": "Register new user"
    },
    "otpFromSms": "One time code from SMS",
    "otpSentToNumber": "We sent a verification code to ",
    "validate": "Validate code"
  }
}
</i18n>

<template>
  <div class="page">
    <FormWrapper>
      <div>
        <TnHeading type="h1" size="l" align="center" class="margin-vertical-2xl">
          {{ t('generic.logIn') }}
        </TnHeading>

        <ErrorMessage :error="error" />

        <div v-if="state === States.BEGIN">
          <form method="post" @submit.prevent="onSubmitUsernamePassword">
            <TnTextField
              id="username"
              v-model="model.username"
              name="username"
              type="text"
              required
              :label="t('fields.username')"
              placeholder="oda@nordmann.no"
            />

            <TnTextField
              id="password"
              v-model="model.password"
              name="password"
              type="password"
              required
              class="margin-top-m"
              :label="t('fields.password')"
              placeholder="*****"
            />

            <TnButton
              id="btnLogin"
              type="submit"
              primary
              block
              class="margin-top-l"
              :text="t('generic.logIn')"
            />
          </form>

          <div class="margin-top-l login-links">
            <TnLink
              id="linkForgotUsername"
              :to="{ name: 'elv-forgot-username', query: { request } }"
              :text="t('links.forgotUsername')"
            />
            <TnLink
              id="linkForgotPassword"
              :to="{ name: 'elv-forgot-password', query: { request } }"
              :text="t('links.forgotPassword')"
            />
            <TnLink
              id="linkRegister"
              :to="{ name: 'elv-register', query: { request } }"
              :text="t('links.register')"
            />
          </div>
        </div>

        <div v-else-if="state === States.OTP">
          <form ref="otpForm" method="post" @submit.prevent="onSubmitOtp">
            <TnHeading
              v-if="otpSentToPhoneNumber"
              type="h2"
              size="2xs"
              align="center"
              class="margin-bottom-m"
            >
              {{ t('otpSentToNumber') + otpSentToPhoneNumber }}
            </TnHeading>

            <OtpAlternatives
              :phone-numbers="otpPhoneNumberAlternatives"
              @otp-number-clicked="sendNewOtp"
            />

            <TnTextField
              id="otpSMS"
              v-model="model.otp"
              name="otp"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              pattern="[0-9]*"
              required
              :label="t('otpFromSms')"
              placeholder="******"
            />

            <TnButton
              id="btnOtp"
              type="submit"
              primary
              block
              class="margin-top-l"
              :text="t('validate')"
            />
          </form>
        </div>
      </div>
    </FormWrapper>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  TnButton,
  TnHeading,
  TnLink,
  TnTextField,
} from '@telenornorgeinternal/telegrafen-vue3-core';

import FormWrapper from '@/components/FormWrapper.vue';
import ErrorMessage from '@/views/elvis/components/errorMessage.vue';
import OtpAlternatives from '@/views/elvis/components/otpalternatives.vue';
import { login, LoginAction, type LoginResult, type OtpPhoneNumber } from '@/views/elvis/api';

const States = { BEGIN: 0, OTP: 1 } as const;

const { t } = useI18n();
const route = useRoute();

const state = ref<number>(States.BEGIN);
const error = ref<unknown>(null);
const otpSentToPhoneNumber = ref<string | null>(null);
const otpPhoneNumberAlternatives = ref<OtpPhoneNumber[]>([]);

const model = reactive({ username: '', password: '', otp: '' });

const request = computed(() => (route.query.request as string) || '');

function applyOtpStep(result: LoginResult) {
  if (result.nextStep === LoginAction.REDIRECT && result.redirect) {
    window.location.href = result.redirect;
    return;
  }

  otpSentToPhoneNumber.value = result.otpSentToPhoneNumber;
  otpPhoneNumberAlternatives.value = result.otpPhoneNumberAlternatives ?? [];
  model.otp = '';
  state.value = States.OTP;
}

function onSubmitUsernamePassword() {
  error.value = null;
  login(model.username, model.password, null, request.value, null)
    .then(applyOtpStep)
    .catch((reason) => (error.value = reason));
}

function sendNewOtp(number: OtpPhoneNumber) {
  error.value = null;
  login(model.username, model.password, null, request.value, number.phoneNumberId)
    .then(applyOtpStep)
    .catch((reason) => (error.value = reason));
}

function onSubmitOtp() {
  error.value = null;
  login(model.username, model.password, model.otp, request.value, null)
    .then((result) => {
      if (result.nextStep === LoginAction.REDIRECT && result.redirect) {
        window.location.href = result.redirect;
      }
    })
    .catch((reason) => (error.value = reason));
}
</script>

<style scoped>
.login-links {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
}
</style>
